import { useCallback, useEffect, useState, useMemo, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Snackbar,
  Alert,
  TextField,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import { ArrowBack, Edit, Delete, Save, Close, Upload, Download, OpenInNew, Transform, ExpandMore, ChevronRight } from '@mui/icons-material'
import { useApi, apiGet } from '../../api/client'
import { fetchRecord, deleteRecord, updateRecord, fetchRelatedRecords, fetchRecordChanges, convertRecord, fetchOwnedRecords, type RecordRow } from '../../api/records'
import { fetchNotes, createNote, updateNote, deleteNote, type NoteRow } from '../../api/notes'
import { fetchAttachments, uploadAttachment, downloadAttachment, deleteAttachment, type AttachmentRow } from '../../api/attachments'
import { fetchMashups, type MashupRow } from '../../api/mashups'
import { useTabStore } from '../../stores/tab.store'
import { ConfirmDialog } from '../../components/shared/ConfirmDialog'
import { FieldRenderer } from '../../components/fields/FieldRenderer'
import { CustomActionButtons } from '../../components/ti/CustomActionButtons'
import { formatDatetimeInTimezone } from '../../utils/timezones'
import { useAuthStore } from '../../stores/auth.store'
import { Country, State, City } from 'country-state-city'
import type { FieldMeta, ApiResponse } from '@crm/shared'

interface RelatedTabConfig {
  label: string
  type: 'related' | 'changes' | 'notes' | 'attachments' | 'mashup' | 'owner_records'
  fieldId?: string
  targetObjectId?: string
  fieldName?: string
  fieldType?: string
}

const OWNER_RECORDS_TAB_MAP: Record<string, string> = {
  leads: 'obj_leads',
  opportunities: 'obj_opportunities',
  customers: 'obj_customers',
}

function parseTiTabs(tiTabs: string | null, fields: FieldMeta[], objectId?: string): RelatedTabConfig[] {
  if (!tiTabs) return []
  let tabNames: string[]
  try {
    tabNames = JSON.parse(tiTabs)
  } catch {
    return []
  }

  return tabNames.map((name) => {
    const lower = name.toLowerCase()
    if (lower === 'changes') return { label: name, type: 'changes' as const }
    if (lower === 'notes') return { label: name, type: 'notes' as const }
    if (lower === 'attachments') return { label: name, type: 'attachments' as const }
    if (lower === 'mashup') return { label: name, type: 'mashup' as const }

    if (objectId === 'obj_employees' && OWNER_RECORDS_TAB_MAP[lower]) {
      return {
        label: name,
        type: 'owner_records' as const,
        targetObjectId: OWNER_RECORDS_TAB_MAP[lower],
      }
    }

    const lookupField = fields.find((f) => {
      if (f.type !== 'lookup' && f.type !== 'multi_lookup' && f.type !== 'reverse_lookup') return false
      if (!f.config) return false
      if (f.label.toLowerCase() !== lower) return false
      try {
        const config = JSON.parse(f.config as unknown as string) as { target_object_id: string; display_field: string }
        return !!config.target_object_id
      } catch {
        return false
      }
    })

    if (lookupField?.config) {
      try {
        const config = JSON.parse(lookupField.config as unknown as string)
        if (config.target_object_id) {
          return {
            label: name,
            type: 'related' as const,
            fieldId: lookupField.field_id,
            targetObjectId: config.target_object_id,
            fieldName: lookupField.name,
            fieldType: lookupField.type,
          }
        }
      } catch { /* ignore */ }
    }

    return { label: name, type: 'related' as const }
  })
}

export function ThingInspector() {
  const { objectId, recordId } = useParams<{ objectId: string; recordId: string }>()
  const navigate = useNavigate()
  const { openTab } = useTabStore()
  const timezone = useAuthStore((s) => s.user?.timezone) ?? 'UTC'

  const { data: schemaData, isLoading: schemaLoading } = useApi<
    ApiResponse<{
      object: { id: string; label: string; label_plural: string; name: string; ti_header_fields: string | null; ti_tabs: string | null }
      fields: FieldMeta[]
      object_permission: string
      field_permissions: Record<string, string>
    }>
  >(objectId ? `/objects/${objectId}/schema` : null)

  const objectInfo = schemaData?.data?.object
  const fields = schemaData?.data?.fields ?? []
  const objectPermission = schemaData?.data?.object_permission ?? 'none'
  const fieldPermissions = schemaData?.data?.field_permissions ?? {}

  const visibleFields = fields.filter((field) => fieldPermissions[field.field_id] !== 'hidden')

  const [record, setRecord] = useState<RecordRow | null>(null)
  const [loading, setLoading] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState<Record<string, unknown>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [apiError, setApiError] = useState('')
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' })
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set())
  const hasInitialized = useRef(false)

  useEffect(() => {
    if (objectId && !hasInitialized.current) {
      const saved = localStorage.getItem(`ti_collapsed_${objectId}`)
      if (saved) {
        try {
          setCollapsedSections(new Set(JSON.parse(saved)))
        } catch { /* ignore */ }
      }
      hasInitialized.current = true
    }
  }, [objectId])

  useEffect(() => {
    if (objectId && hasInitialized.current) {
      localStorage.setItem(`ti_collapsed_${objectId}`, JSON.stringify([...collapsedSections]))
    }
  }, [objectId, collapsedSections])

  const toggleSection = useCallback((sectionName: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev)
      if (next.has(sectionName)) {
        next.delete(sectionName)
      } else {
        next.add(sectionName)
      }
      return next
    })
  }, [])

  const tiTabs = useMemo(() => parseTiTabs(objectInfo?.ti_tabs ?? null, fields, objectId ?? undefined), [objectInfo?.ti_tabs, fields, objectId])

  const loadRecord = useCallback(async () => {
    if (!objectId || !recordId) return
    setLoading(true)
    try {
      const result = await fetchRecord(objectId, recordId)
      setRecord(result.data)
    } catch {
      setRecord(null)
    } finally {
      setLoading(false)
    }
  }, [objectId, recordId])

  useEffect(() => {
    loadRecord()
  }, [loadRecord])

  useEffect(() => {
    if (objectInfo && recordId) {
      openTab({
        objectId: objectInfo.id,
        objectLabel: objectInfo.label,
        recordId,
        path: `/o/${objectInfo.id}/${recordId}`,
      })
    }
  }, [objectInfo, recordId, openTab])

  const headerFieldIds = objectInfo?.ti_header_fields
    ? JSON.parse(objectInfo.ti_header_fields)
    : []
  const headerFields = visibleFields.filter((field) => headerFieldIds.includes(field.field_id))

  const sections = useMemo(() => {
    const map = new Map<string, FieldMeta[]>()
    for (const field of visibleFields) {
      if (field.type === 'reverse_lookup' || field.type === 'multi_lookup') continue
      const section = field.section || 'General'
      if (!map.has(section)) map.set(section, [])
      map.get(section)!.push(field)
    }
    for (const [, sectionFields] of map) {
      sectionFields.sort((a, b) => {
        if ((a.type === 'long_text' || a.type === 'json') && b.type !== 'long_text' && b.type !== 'json') return 1
        if (a.type !== 'long_text' && a.type !== 'json' && (b.type === 'long_text' || b.type === 'json')) return -1
        return 0
      })
    }
    return map
  }, [visibleFields])

  const systemFields = useMemo(() => ({
    id: record?.id ?? '',
    seq_id: record?.seq_id ?? '',
    owner_id: record?.owner_id ?? '',
    created_at: record?.created_at ?? '',
    updated_at: record?.updated_at ?? '',
    created_by: record?.created_by_name ?? '',
    updated_by: record?.updated_by ?? '',
  }), [record])

  const editableFields = useMemo(
    () =>
      fields.filter((f) => {
        if (f.type === 'reverse_lookup' || f.type === 'multi_lookup') return false
        const perm = fieldPermissions[f.field_id]
        return perm === 'read_write' || perm === undefined
      }),
    [fields, fieldPermissions],
  )

  const handleStartEdit = useCallback(() => {
    if (!record) return
    const validFieldNames = new Set(fields.map((f) => f.name))
    const filteredData: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(record.data)) {
      if (validFieldNames.has(key)) {
        filteredData[key] = value
      }
    }
    setFormData(filteredData)
    setErrors({})
    setApiError('')
    setIsEditing(true)
  }, [record, fields])

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false)
    setFormData({})
    setErrors({})
    setApiError('')
  }, [])

  const handleFieldChange = useCallback((fieldName: string, value: unknown) => {
    setFormData((prev) => {
      const next = { ...prev, [fieldName]: value }
      if (fieldName === 'country_region') {
        next.city = ''
      }
      return next
    })
    setErrors((prev) => {
      const next = { ...prev }
      delete next[fieldName]
      return next
    })
  }, [])

  // 默认国家选项（如果数据库没配置就用这个）
  const defaultCountryOptions = useMemo(() => Country.getAllCountries().map((c) => ({ value: c.isoCode, label: `${c.isoCode} - ${c.name}` })), [])

  // 从 field.options 解析选项
  const getOptionsFromField = useCallback((field: FieldMeta): Array<{ value: string; label: string }> => {
    if (!field.options) return []
    try {
      const parsed = JSON.parse(field.options as unknown as string)
      return parsed.options ?? parsed
    } catch {
      return []
    }
  }, [])

  const currentCountry = isEditing ? (formData.country_region as string | undefined) : (record?.data?.country_region as string | undefined)

  const cityOptions = useMemo(() => {
    if (!currentCountry) return []
    const citySet = new Set<string>()
    for (const state of State.getStatesOfCountry(currentCountry)) {
      for (const city of City.getCitiesOfState(currentCountry, state.isoCode)) {
        citySet.add(city.name)
      }
    }
    return Array.from(citySet).sort().map((name) => ({ value: name, label: name }))
  }, [currentCountry])

  const getFieldDynamicOptions = useCallback((field: FieldMeta): Array<{ value: string; label: string }> | undefined => {
    if (field.name === 'country_region' && field.type === 'select') {
      const fieldOptions = getOptionsFromField(field)
      return fieldOptions.length > 0 ? fieldOptions : defaultCountryOptions
    }
    if (field.config) {
      try {
        const config = JSON.parse(field.config as unknown as string)
        if (config.depends_on === 'country_region' && field.type === 'select') return cityOptions
      } catch { /* ignore */ }
    }
    return undefined
  }, [defaultCountryOptions, cityOptions, getOptionsFromField])

  const isFieldDisabled = useCallback((field: FieldMeta): boolean => {
    if (field.config) {
      try {
        const config = JSON.parse(field.config as unknown as string)
        if (config.depends_on === 'country_region') return !currentCountry
      } catch { /* ignore */ }
    }
    return false
  }, [currentCountry])

  const handleSaveEdit = useCallback(async () => {
    if (!objectId || !recordId || !record) return
    const newErrors: Record<string, string> = {}
    for (const field of editableFields) {
      if (field.is_required && (formData[field.name] === undefined || formData[field.name] === null || formData[field.name] === '')) {
        newErrors[field.name] = `${field.label} is required`
      }
    }
    setErrors(newErrors)
    if (Object.keys(newErrors).length > 0) return

    setSaving(true)
    setApiError('')
    try {
      await updateRecord(objectId, recordId, { data: formData, version: record.version })
      setIsEditing(false)
      setFormData({})
      setErrors({})
      setApiError('')
      loadRecord()
      setToast({ open: true, message: 'Record updated', severity: 'success' })
    } catch (err) {
      setApiError(err instanceof Error ? err.message : 'Failed to save record')
    } finally {
      setSaving(false)
    }
  }, [objectId, recordId, record, editableFields, formData, loadRecord])

  const handleDelete = useCallback(async () => {
    if (!objectId || !recordId) return
    try {
      await deleteRecord(objectId, recordId)
      setToast({ open: true, message: 'Record deleted', severity: 'success' })
      setDeleteOpen(false)
      navigate(`/o/${objectId}`)
    } catch (err) {
      setToast({ open: true, message: err instanceof Error ? err.message : 'Delete failed', severity: 'error' })
    }
  }, [objectId, recordId, navigate])

  const [convertOpen, setConvertOpen] = useState(false)
  const [converting, setConverting] = useState(false)

  const convertTarget = useMemo(() => {
    if (objectId === 'obj_leads') return { type: 'opportunity', label: 'Opportunity', targetObjectId: 'obj_opportunities' }
    if (objectId === 'obj_opportunities') return { type: 'customer', label: 'Customer', targetObjectId: 'obj_customers' }
    return null
  }, [objectId])

  const isConverted = useMemo(() => {
    if (!record) return false
    const data = typeof record.data === 'string' ? JSON.parse(record.data) : record.data
    return data.converted === true || data.converted === 'true'
  }, [record])

  const handleConvert = useCallback(async () => {
    if (!objectId || !recordId || !convertTarget) return
    setConverting(true)
    try {
      const result = await convertRecord(objectId, recordId, convertTarget.type)
      setToast({ open: true, message: `Converted to ${convertTarget.label} successfully`, severity: 'success' })
      setConvertOpen(false)
      const targetId = result.data?.opportunityId || result.data?.customerId
      if (targetId) {
        navigate(`/o/${convertTarget.targetObjectId}/${targetId}`)
      } else {
        loadRecord()
      }
    } catch (err) {
      setToast({ open: true, message: err instanceof Error ? err.message : 'Conversion failed', severity: 'error' })
    } finally {
      setConverting(false)
    }
  }, [objectId, recordId, convertTarget, navigate, loadRecord])

  if (schemaLoading || loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
  }

  if (!record) {
    return <Box sx={{ p: 4, textAlign: 'center' }}><Typography sx={{ color: 'text.secondary' }}>Record not found</Typography></Box>
  }

  const canEdit = objectPermission === 'read_write' || objectPermission === 'full'
  const canDelete = objectPermission === 'full'

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ flexShrink: 0, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton size="small" onClick={() => navigate(`/o/${objectId}`)}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {headerFields.length > 0 ? headerFields.map((field) => (
              <Typography key={field.field_id} variant="h6" sx={{ fontWeight: 700 }}>
                {String(record.data[field.name] ?? '')}
              </Typography>
            )) : (
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{objectInfo?.label ?? 'Record'} Detail</Typography>
            )}
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            ID: {record.id} · v{record.version} · Updated: {formatDatetimeInTimezone(record.updated_at, timezone)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {!isEditing && (
            <CustomActionButtons
              objectId={objectId ?? ''}
              recordId={recordId ?? ''}
              recordData={record.data}
              systemFields={systemFields}
              fields={fields}
              fieldPermissions={fieldPermissions}
            />
          )}
          {canEdit && !isEditing && (
            <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={handleStartEdit}><Edit /></IconButton></Tooltip>
          )}
          {isEditing && (
            <>
              <Tooltip title="Save"><IconButton size="small" color="primary" onClick={handleSaveEdit} disabled={saving}><Save /></IconButton></Tooltip>
              <Tooltip title="Cancel"><IconButton size="small" onClick={handleCancelEdit} disabled={saving}><Close /></IconButton></Tooltip>
            </>
          )}
          {canDelete && !isEditing && (
            <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleteOpen(true)}><Delete /></IconButton></Tooltip>
          )}
          {isConverted && (
            <Tooltip title="Converted">
              <IconButton size="small" disabled sx={{ color: 'success.main' }}>
                <Chip label="Converted" color="success" size="small" variant="outlined" sx={{ m: 0 }} />
              </IconButton>
            </Tooltip>
          )}
          {convertTarget && canEdit && !isEditing && !isConverted && (
            <Button
              size="small"
              variant="outlined"
              color="success"
              startIcon={<Transform />}
              onClick={() => setConvertOpen(true)}
            >
              Convert to {convertTarget.label}
            </Button>
          )}
        </Box>
      </Box>

      {apiError && isEditing && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>{apiError}</Alert>
      )}

      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {Array.from(sections.entries()).map(([sectionName, sectionFields]) => {
        const isCollapsed = collapsedSections.has(sectionName)
        return (
          <Paper key={sectionName} variant="outlined" sx={{ mb: 2 }}>
            <Box 
              sx={{ px: 2, py: 1, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider', cursor: 'pointer' }}
              onClick={() => toggleSection(sectionName)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isCollapsed ? <ChevronRight fontSize="small" /> : <ExpandMore fontSize="small" />}
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{sectionName}</Typography>
              </Box>
            </Box>
            {!isCollapsed && (
              <Box sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  {sectionFields.map((field) => {
                    const perm = fieldPermissions[field.field_id]
                    const isSystemField = objectId === 'obj_leads' && field.name === 'converted'
                    const isFieldEditable = isEditing && !isSystemField && (perm === 'read_write' || perm === undefined)
                    const value = isEditing ? formData[field.name] : record.data[field.name]
                    const isEmpty = value == null || value === '' || (Array.isArray(value) && value.length === 0)
                    return (
                      <Grid key={field.field_id} size={{ xs: 12, sm: 6, md: 3 }}>
                        <Box>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                            {field.label}
                            {!!field.is_required && <span style={{ color: 'red' }}> *</span>}
                            {(perm === 'readonly' || isSystemField) && <span style={{ color: 'orange', fontSize: 10, marginLeft: 4 }}>🔒</span>}
                          </Typography>
                          <Box sx={{ mt: 0.25 }}>
                            {isFieldEditable ? (
                              <FieldRenderer
                                field={field}
                                value={value}
                                mode="edit"
                                onChange={(v) => handleFieldChange(field.name, v)}
                                error={errors[field.name]}
                                hideLabel
                                dynamicOptions={getFieldDynamicOptions(field)}
                                disabled={isFieldDisabled(field)}
                              />
                            ) : isEmpty ? (
                              <Typography sx={{ color: 'text.disabled', userSelect: 'none' }}>—</Typography>
                            ) : (
                              <FieldRenderer field={field} value={value} mode="view" dynamicOptions={getFieldDynamicOptions(field)} />
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    )
                  })}
                  {sectionName === 'General' && !isEditing && (
                    <>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Box>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>Created By</Typography>
                          <Box sx={{ mt: 0.25 }}>
                            <Typography variant="body2">{record.created_by_name || '—'}</Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Box>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>Created On</Typography>
                          <Box sx={{ mt: 0.25 }}>
                            <Typography variant="body2">{formatDatetimeInTimezone(record.created_at, timezone)}</Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>
            )}
          </Paper>
        )
      })}

      {tiTabs.length > 0 && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
            {tiTabs.map((tab) => (
              <Tab key={tab.label} label={tab.label} />
            ))}
          </Tabs>
        </Box>
      )}

      {tiTabs.length > 0 && activeTab >= 0 && activeTab < tiTabs.length && (
        <TabContent tab={tiTabs[activeTab]} objectId={objectId!} recordId={recordId!} />
      )}
      </Box>

      <ConfirmDialog
        open={deleteOpen}
        title="Delete Record"
        message="Are you sure you want to delete this record? This action can be undone by an admin."
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={handleDelete}
        onCancel={() => setDeleteOpen(false)}
      />

      <ConfirmDialog
        open={convertOpen}
        title={`Convert to ${convertTarget?.label ?? ''}`}
        message={`This will create a new ${convertTarget?.label ?? ''} record from this ${objectId === 'obj_leads' ? 'Lead' : 'Opportunity'}. The original record will be marked as converted. Continue?`}
        confirmLabel="Convert"
        confirmColor="success"
        onConfirm={handleConvert}
        onCancel={() => setConvertOpen(false)}
      />

      <Snackbar open={toast.open} autoHideDuration={4000} onClose={() => setToast((p) => ({ ...p, open: false }))}>
        <Alert severity={toast.severity} onClose={() => setToast((p) => ({ ...p, open: false }))}>{toast.message}</Alert>
      </Snackbar>
    </Box>
  )
}

function TabContent({ tab, objectId, recordId }: { tab: RelatedTabConfig; objectId: string; recordId: string }) {
  if (tab.type === 'changes') {
    return <ChangesTab objectId={objectId} recordId={recordId} />
  }

  if (tab.type === 'related' && tab.fieldId) {
    return <RelatedRecordsTab objectId={objectId} recordId={recordId} fieldId={tab.fieldId} targetObjectId={tab.targetObjectId} fieldName={tab.fieldName} fieldType={tab.fieldType} />
  }

  if (tab.type === 'owner_records' && tab.targetObjectId) {
    return <OwnedRecordsTab objectId={objectId} recordId={recordId} targetObjectId={tab.targetObjectId} />
  }

  if (tab.type === 'notes') {
    return <NotesTab objectId={objectId} recordId={recordId} />
  }

  if (tab.type === 'attachments') {
    return <AttachmentsTab objectId={objectId} recordId={recordId} />
  }

  if (tab.type === 'mashup') {
    return <MashupTab objectId={objectId} />
  }

  return (
    <Box sx={{ p: 3, textAlign: 'center' }}>
      <Typography sx={{ color: 'text.secondary' }}>{tab.label} tab coming soon</Typography>
    </Box>
  )
}

function ChangesTab({ objectId, recordId }: { objectId: string; recordId: string }) {
  const timezone = useAuthStore((s) => s.user?.timezone) ?? 'UTC'
  const [changes, setChanges] = useState<Array<Record<string, unknown>>>([])
  const [loading, setLoading] = useState(true)
  const [hasMore, setHasMore] = useState(false)
  const [cursor, setCursor] = useState<string | null>(null)

  const loadChanges = useCallback(async (cur?: string) => {
    setLoading(true)
    try {
      const result = await fetchRecordChanges(objectId, recordId, cur)
      if (cur) {
        setChanges((prev) => [...prev, ...result.data])
      } else {
        setChanges(result.data)
      }
      setHasMore(result.pagination.has_more)
      setCursor(result.pagination.next_cursor)
    } catch { /* ignore */ } finally {
      setLoading(false)
    }
  }, [objectId, recordId])

  useEffect(() => {
    loadChanges()
  }, [loadChanges])

  if (loading && changes.length === 0) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={24} /></Box>
  }

  if (changes.length === 0) {
    return <Box sx={{ p: 3, textAlign: 'center' }}><Typography sx={{ color: 'text.secondary' }}>No changes recorded</Typography></Box>
  }

  return (
    <Box>
      {changes.map((change, i) => {
        const changeType = String(change.change_type ?? '')
        const fieldName = String(change.field_label ?? change.field_name ?? '')
        const oldValue = change.old_value ? String(change.old_value) : ''
        const newValue = change.new_value ? String(change.new_value) : ''
        const changedAt = String(change.changed_at ?? '')
        const changedBy = String(change.changed_by_name ?? change.changed_by ?? '')

        return (
          <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, py: 1, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: changeType === 'create' ? 'success.main' : changeType === 'delete' ? 'error.main' : 'primary.main', mt: 0.5, flexShrink: 0 }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body2">
                <Chip label={changeType} size="small" variant="outlined" sx={{ mr: 1 }} />
                {fieldName && <strong>{fieldName}</strong>}
                {oldValue && <span style={{ color: 'error.main', textDecoration: 'line-through', marginLeft: 8 }}>{oldValue}</span>}
                {newValue && <span style={{ color: 'success.main', marginLeft: 8 }}>{newValue}</span>}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {formatDatetimeInTimezone(changedAt, timezone)} · by {changedBy}
              </Typography>
            </Box>
          </Box>
        )
      })}
      {hasMore && (
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button size="small" onClick={() => loadChanges(cursor ?? undefined)} disabled={loading}>
            Load More
          </Button>
        </Box>
      )}
    </Box>
  )
}

function NotesTab({ objectId, recordId }: { objectId: string; recordId: string }) {
  const timezone = useAuthStore((s) => s.user?.timezone) ?? 'UTC'
  const [notes, setNotes] = useState<NoteRow[]>([])
  const [loading, setLoading] = useState(true)
  const [newContent, setNewContent] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editContent, setEditContent] = useState('')

  const loadNotes = useCallback(async () => {
    setLoading(true)
    try {
      const result = await fetchNotes(objectId, recordId)
      setNotes(result.data ?? [])
    } catch { /* ignore */ } finally {
      setLoading(false)
    }
  }, [objectId, recordId])

  useEffect(() => {
    loadNotes()
  }, [loadNotes])

  const handleCreate = useCallback(async () => {
    if (!newContent.trim()) return
    try {
      await createNote(objectId, recordId, newContent.trim())
      setNewContent('')
      loadNotes()
    } catch { /* ignore */ }
  }, [objectId, recordId, newContent, loadNotes])

  const handleUpdate = useCallback(async (noteId: string) => {
    if (!editContent.trim()) return
    try {
      await updateNote(objectId, recordId, noteId, editContent.trim())
      setEditingId(null)
      setEditContent('')
      loadNotes()
    } catch { /* ignore */ }
  }, [objectId, recordId, editContent, loadNotes])

  const handleDelete = useCallback(async (noteId: string) => {
    try {
      await deleteNote(objectId, recordId, noteId)
      loadNotes()
    } catch { /* ignore */ }
  }, [objectId, recordId, loadNotes])

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={24} /></Box>
  }

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <TextField
          size="small"
          multiline
          rows={2}
          fullWidth
          placeholder="Add a note..."
          value={newContent}
          onChange={(e) => setNewContent(e.target.value)}
        />
        <Button size="small" variant="contained" onClick={handleCreate} disabled={!newContent.trim()} sx={{ mt: 1 }}>
          Add Note
        </Button>
      </Box>
      {notes.map((note) => (
        <Paper key={note.id} variant="outlined" sx={{ p: 2, mb: 1 }}>
          {editingId === note.id ? (
            <Box>
              <TextField size="small" multiline rows={2} fullWidth value={editContent} onChange={(e) => setEditContent(e.target.value)} />
              <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                <Button size="small" variant="contained" onClick={() => handleUpdate(note.id)}>Save</Button>
                <Button size="small" onClick={() => setEditingId(null)}>Cancel</Button>
              </Box>
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{note.content}</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {formatDatetimeInTimezone(note.created_at, timezone)} · by {note.created_by}
                </Typography>
                <Box>
                  <Button size="small" onClick={() => { setEditingId(note.id); setEditContent(note.content) }}>Edit</Button>
                  <Button size="small" color="error" onClick={() => handleDelete(note.id)}>Delete</Button>
                </Box>
              </Box>
            </Box>
          )}
        </Paper>
      ))}
      {notes.length === 0 && (
        <Typography sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>No notes yet</Typography>
      )}
    </Box>
  )
}

interface RelatedColumnConfig {
  key: string
  label: string
  render: (rec: RecordRow) => React.ReactNode
  width?: string
}

function getRelatedColumns(targetObjectId: string, timezone?: string): RelatedColumnConfig[] {
  const tz = timezone ?? 'UTC'
  if (targetObjectId === 'obj_contacts') {
    return [
      { key: 'id', label: 'ID', render: (rec) => <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{rec.id}</Typography>, width: '25%' },
      { key: 'name', label: 'Name', render: (rec) => String(rec.data.name ?? rec.data.formatted_name ?? '—'), width: '25%' },
      { key: 'email', label: 'Email', render: (rec) => String(rec.data.email ?? '—'), width: '25%' },
      { key: 'phone', label: 'Phone', render: (rec) => String(rec.data.phone ?? rec.data.mobile ?? '—'), width: '25%' },
    ]
  }
  if (targetObjectId === 'obj_leads') {
    return [
      { key: 'id', label: 'ID', render: (rec) => <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{rec.id}</Typography>, width: '25%' },
      { key: 'name', label: 'Name', render: (rec) => String(rec.data.name ?? rec.data.formatted_name ?? '—'), width: '25%' },
      { key: 'status', label: 'Status', render: (rec) => <Chip label={String(rec.data.status ?? '—')} size="small" variant="outlined" />, width: '25%' },
      { key: 'priority', label: 'Priority', render: (rec) => String(rec.data.priority ?? '—'), width: '25%' },
    ]
  }
  if (targetObjectId === 'obj_opportunities') {
    return [
      { key: 'id', label: 'ID', render: (rec) => <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{rec.id}</Typography>, width: '20%' },
      { key: 'name', label: 'Name', render: (rec) => String(rec.data.name ?? rec.data.formatted_name ?? '—'), width: '20%' },
      { key: 'stage', label: 'Stage', render: (rec) => <Chip label={String(rec.data.stage ?? '—')} size="small" variant="outlined" />, width: '20%' },
      { key: 'close_date', label: 'Close Date', render: (rec) => String(rec.data.close_date ?? '—'), width: '20%' },
      { key: 'priority', label: 'Priority', render: (rec) => String(rec.data.priority ?? '—'), width: '20%' },
    ]
  }
  return [
    { key: 'id', label: 'ID', render: (rec) => <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{rec.id}</Typography>, width: '33.33%' },
    { key: 'name', label: 'Name', render: (rec) => String(rec.data.name ?? rec.data.formatted_name ?? '—'), width: '33.33%' },
    { key: 'updated', label: 'Updated', render: (rec) => formatDatetimeInTimezone(rec.updated_at, tz), width: '33.34%' },
  ]
}

function RelatedRecordsTab({ objectId, recordId, fieldId, targetObjectId, fieldName, fieldType }: { objectId: string; recordId: string; fieldId: string; targetObjectId?: string; fieldName?: string; fieldType?: string }) {
  const timezone = useAuthStore((s) => s.user?.timezone) ?? 'UTC'
  const [records, setRecords] = useState<RecordRow[]>([])
  const [loading, setLoading] = useState(true)
  const [hasMore, setHasMore] = useState(false)
  const [cursor, setCursor] = useState<string | null>(null)
  const navigate = useNavigate()
  const [addDialogOpen, setAddDialogOpen] = useState(false)

  const loadRecords = useCallback(async (cur?: string) => {
    setLoading(true)
    try {
      const result = await fetchRelatedRecords(objectId, recordId, fieldId, cur)
      const data = result.data ?? []
      if (cur) {
        setRecords((prev) => [...prev, ...data])
      } else {
        setRecords(data)
      }
      setHasMore(result.pagination.has_more)
      setCursor(result.pagination.next_cursor)
    } catch { /* ignore */ } finally {
      setLoading(false)
    }
  }, [objectId, recordId, fieldId])

  useEffect(() => {
    loadRecords()
  }, [loadRecords])

  const handleAddRecords = useCallback(async (selectedIds: string[]) => {
    if (!fieldName || !fieldType || fieldType !== 'multi_lookup' || !targetObjectId || selectedIds.length === 0) return
    try {
      const recordResult = await fetchRecord(objectId, recordId)
      const currentData = recordResult.data.data as Record<string, unknown>
      const currentValues = (Array.isArray(currentData[fieldName]) ? currentData[fieldName] : []) as string[]
      const merged = [...new Set([...currentValues, ...selectedIds])]
      await updateRecord(objectId, recordId, { data: { ...currentData, [fieldName]: merged }, version: recordResult.data.version })
      loadRecords()
      setToast({ open: true, message: 'Records added', severity: 'success' })
    } catch {
      setToast({ open: true, message: 'Failed to add records', severity: 'error' })
    }
  }, [objectId, recordId, fieldName, fieldType, targetObjectId, loadRecords])

  const handleRemoveRecord = useCallback(async (removeRecordId: string) => {
    if (!fieldName || !fieldType || fieldType !== 'multi_lookup' || !targetObjectId) return
    try {
      const recordResult = await fetchRecord(objectId, recordId)
      const currentData = recordResult.data.data as Record<string, unknown>
      const currentValues = (Array.isArray(currentData[fieldName]) ? currentData[fieldName] : []) as string[]
      const filtered = currentValues.filter((id) => id !== removeRecordId)
      await updateRecord(objectId, recordId, { data: { ...currentData, [fieldName]: filtered }, version: recordResult.data.version })
      loadRecords()
      setToast({ open: true, message: 'Record removed', severity: 'success' })
    } catch {
      setToast({ open: true, message: 'Failed to remove record', severity: 'error' })
    }
  }, [objectId, recordId, fieldName, fieldType, targetObjectId, loadRecords])

  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' })

  const canAdd = fieldType === 'multi_lookup' && !!targetObjectId && !!fieldName

  if (loading && records.length === 0) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={24} /></Box>
  }

  return (
    <Box>
      {canAdd && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" size="small" startIcon={<span style={{ fontSize: 18 }}>+</span>} onClick={() => setAddDialogOpen(true)}>
            Add
          </Button>
        </Box>
      )}
      {records.length === 0 && !loading ? (
        <Box sx={{ p: 3, textAlign: 'center' }}><Typography sx={{ color: 'text.secondary' }}>No related records</Typography></Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {getRelatedColumns(targetObjectId ?? '', timezone).map((col) => (
                  <TableCell key={col.key} sx={{ fontWeight: 700 }} width={col.width}>{col.label}</TableCell>
                ))}
                {canAdd && <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((rec) => (
                <TableRow
                  key={rec.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/o/${rec.object_id}/${rec.id}`)}
                >
                  {getRelatedColumns(targetObjectId ?? '', timezone).map((col) => (
                    <TableCell key={col.key}>{col.render(rec)}</TableCell>
                  ))}
                  {canAdd && (
                    <TableCell align="right">
                      <Tooltip title="Remove">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => { e.stopPropagation(); handleRemoveRecord(rec.id) }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {hasMore && (
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button size="small" onClick={() => loadRecords(cursor ?? undefined)} disabled={loading}>
            Load More
          </Button>
        </Box>
      )}
      {canAdd && targetObjectId && (
        <AddRelatedRecordDialog
          open={addDialogOpen}
          onClose={() => setAddDialogOpen(false)}
          onAdd={handleAddRecords}
          targetObjectId={targetObjectId}
          existingRecordIds={records.map((r) => r.id)}
        />
      )}
      <Snackbar open={toast.open} autoHideDuration={4000} onClose={() => setToast((p) => ({ ...p, open: false }))}>
        <Alert severity={toast.severity} onClose={() => setToast((p) => ({ ...p, open: false }))}>{toast.message}</Alert>
      </Snackbar>
    </Box>
  )
}

function OwnedRecordsTab({ objectId, recordId, targetObjectId }: { objectId: string; recordId: string; targetObjectId: string }) {
  const timezone = useAuthStore((s) => s.user?.timezone) ?? 'UTC'
  const [records, setRecords] = useState<RecordRow[]>([])
  const [loading, setLoading] = useState(true)
  const [hasMore, setHasMore] = useState(false)
  const [cursor, setCursor] = useState<string | null>(null)
  const navigate = useNavigate()
  const columns = getRelatedColumns(targetObjectId, timezone)

  const loadRecords = useCallback(async (cur?: string) => {
    setLoading(true)
    try {
      const result = await fetchOwnedRecords(objectId, recordId, targetObjectId, cur)
      const data = result.data ?? []
      if (cur) {
        setRecords((prev) => [...prev, ...data])
      } else {
        setRecords(data)
      }
      setHasMore(result.pagination.has_more)
      setCursor(result.pagination.next_cursor)
    } catch { /* ignore */ } finally {
      setLoading(false)
    }
  }, [objectId, recordId, targetObjectId])

  useEffect(() => {
    loadRecords()
  }, [loadRecords])

  if (loading && records.length === 0) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={24} /></Box>
  }

  if (records.length === 0) {
    return <Box sx={{ p: 3, textAlign: 'center' }}><Typography sx={{ color: 'text.secondary' }}>No owned records</Typography></Box>
  }

  return (
    <Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.key} sx={{ fontWeight: 700 }} width={col.width}>{col.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((rec) => (
              <TableRow
                key={rec.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/o/${targetObjectId}/${rec.id}`)}
              >
                {columns.map((col) => (
                  <TableCell key={col.key}>{col.render(rec)}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {hasMore && (
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button size="small" onClick={() => loadRecords(cursor ?? undefined)} disabled={loading}>
            Load More
          </Button>
        </Box>
      )}
    </Box>
  )
}

function AttachmentsTab({ objectId, recordId }: { objectId: string; recordId: string }) {
  const timezone = useAuthStore((s) => s.user?.timezone) ?? 'UTC'
  const [attachments, setAttachments] = useState<AttachmentRow[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [deleteOpen, setDeleteOpen] = useState<string | null>(null)

  const loadAttachments = useCallback(async () => {
    setLoading(true)
    try {
      const result = await fetchAttachments(objectId, recordId)
      setAttachments(result.data ?? [])
    } catch { /* ignore */ } finally {
      setLoading(false)
    }
  }, [objectId, recordId])

  useEffect(() => {
    loadAttachments()
  }, [loadAttachments])

  const handleUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    setUploadProgress(0)
    try {
      await uploadAttachment(objectId, recordId, file, (percent) => setUploadProgress(percent))
      loadAttachments()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setUploading(false)
      setUploadProgress(0)
      e.target.value = ''
    }
  }, [objectId, recordId, loadAttachments])

  const handleDelete = useCallback(async (attachmentId: string) => {
    try {
      await deleteAttachment(attachmentId)
      setDeleteOpen(null)
      loadAttachments()
    } catch { /* ignore */ }
  }, [loadAttachments])

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={24} /></Box>
  }

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button variant="contained" size="small" component="label" startIcon={<Upload />} disabled={uploading}>
          Upload File
          <input type="file" hidden onChange={handleUpload} />
        </Button>
        {uploading && <Box sx={{ flexGrow: 1 }}><LinearProgress variant="determinate" value={uploadProgress} /></Box>}
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>File Name</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>Uploaded By</TableCell>
              <TableCell>Date</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {attachments.length === 0 && !uploading ? (
              <TableRow>
                <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  No attachments
                </TableCell>
              </TableRow>
            ) : (
              attachments.map((att) => (
                <TableRow key={att.id}>
                  <TableCell>{att.file_name}</TableCell>
                  <TableCell>{formatFileSize(att.file_size)}</TableCell>
                  <TableCell>{att.uploaded_by_name || att.uploaded_by}</TableCell>
                  <TableCell>{formatDatetimeInTimezone(att.created_at, timezone)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Download">
                      <IconButton size="small" onClick={() => downloadAttachment(att.id, att.file_name)}>
                        <Download fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setDeleteOpen(att.id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmDialog
        open={!!deleteOpen}
        title="Delete Attachment"
        message="Are you sure you want to delete this attachment? This action cannot be undone."
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={() => deleteOpen && handleDelete(deleteOpen)}
        onCancel={() => setDeleteOpen(null)}
      />
    </Box>
  )
}

function MashupTab({ objectId }: { objectId: string }) {
  const [mashups, setMashups] = useState<MashupRow[]>([])
  const [loading, setLoading] = useState(true)

  const loadMashups = useCallback(async () => {
    setLoading(true)
    try {
      const result = await fetchMashups(objectId)
      setMashups(result.data ?? [])
    } catch { /* ignore */ } finally {
      setLoading(false)
    }
  }, [objectId])

  useEffect(() => {
    loadMashups()
  }, [loadMashups])

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={24} /></Box>
  }

  if (mashups.length === 0) {
    return <Typography sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>No embedded pages configured</Typography>
  }

  return (
    <Box>
      {mashups.map((mashup) => (
        <Box key={mashup.id} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2">{mashup.title}</Typography>
            <Tooltip title="Open in new tab">
              <IconButton size="small" component="a" href={mashup.url} target="_blank" rel="noopener noreferrer">
                <OpenInNew fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
            <iframe
              src={mashup.url}
              title={mashup.title}
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              style={{ width: '100%', height: 400, border: 'none' }}
            />
          </Box>
        </Box>
      ))}
    </Box>
  )
}

function AddRelatedRecordDialog({ open, onClose, onAdd, targetObjectId, existingRecordIds }: {
  open: boolean
  onClose: () => void
  onAdd: (selectedIds: string[]) => void
  targetObjectId: string
  existingRecordIds: string[]
}) {
  const [keyword, setKeyword] = useState('')
  const [options, setOptions] = useState<Array<{ id: string; seq_id: string; name: string; sub?: string }>>([])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)
  const [adding, setAdding] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    if (open) {
      setKeyword('')
      setOptions([])
      setSelected(new Set())
    }
  }, [open])

  const searchRecords = useCallback(async (q: string) => {
    if (!targetObjectId) return
    setLoading(true)
    try {
      const params = new URLSearchParams({ limit: '20' })
      if (q.trim()) params.set('keyword', q.trim())
      const result = await apiGet<{ data: Array<{ id: string; data: Record<string, unknown> }> }>(
        `/objects/${targetObjectId}/records?${params.toString()}`,
      )
      const items = (result.data ?? []).map((r: Record<string, unknown>) => ({
        id: String(r.uuid ?? r.id),
        seq_id: String(r.id ?? ''),
        name: String((r.data as Record<string, unknown>)?.name ?? (r.data as Record<string, unknown>)?.formatted_name ?? r.uuid ?? r.id),
        sub: String((r.data as Record<string, unknown>)?.email ?? (r.data as Record<string, unknown>)?.phone ?? ''),
      }))
      setOptions(items)
    } catch { /* ignore */ } finally {
      setLoading(false)
    }
  }, [targetObjectId])

  const debouncedSearch = useCallback((q: string) => {
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => searchRecords(q), 300)
  }, [searchRecords])

  useEffect(() => {
    return () => clearTimeout(debounceRef.current)
  }, [])

  useEffect(() => {
    if (open) searchRecords('')
  }, [open, searchRecords])

  const toggleSelect = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const handleAdd = useCallback(async () => {
    if (selected.size === 0) return
    setAdding(true)
    try {
      await onAdd([...selected])
      onClose()
    } finally {
      setAdding(false)
    }
  }, [selected, onAdd, onClose])

  const filteredOptions = options.filter((o) => !existingRecordIds.includes(o.id))

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Record</DialogTitle>
      <DialogContent dividers>
        <TextField
          size="small"
          fullWidth
          placeholder="Search..."
          value={keyword}
          onChange={(e) => { setKeyword(e.target.value); debouncedSearch(e.target.value) }}
          sx={{ mb: 2 }}
          autoFocus
        />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>
        ) : filteredOptions.length === 0 ? (
          <Typography sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>No records found</Typography>
        ) : (
          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            {filteredOptions.map((opt) => (
              <Box
                key={opt.id}
                onClick={() => toggleSelect(opt.id)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75,
                  cursor: 'pointer', borderRadius: 1,
                  bgcolor: selected.has(opt.id) ? 'action.selected' : 'transparent',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <input type="checkbox" checked={selected.has(opt.id)} onChange={() => toggleSelect(opt.id)} style={{ accentColor: '#1976d2' }} />
                <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  {opt.seq_id && <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'monospace', fontSize: '0.8rem', flexShrink: 0 }}>#{opt.seq_id}</Typography>}
                  <Typography variant="body2" sx={{ flexShrink: 0 }}>{opt.name}</Typography>
                  {opt.sub && <Typography variant="body2" sx={{ color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opt.sub}</Typography>}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={adding}>Cancel</Button>
        <Button onClick={handleAdd} variant="contained" disabled={adding || selected.size === 0}>
          {adding ? <CircularProgress size={20} /> : `Add (${selected.size})`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
